import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Mall, Warehouse } from '@common/services/commondata';
import { oDataService } from '@common/services/odata';
import { Pagination } from '@common/types/pagination.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, forkJoin, Observable, tap, map, filter, switchMap } from 'rxjs';
export interface LocationLookupDto {
    id: number;
    code: string;
    name: string;
}
export interface LevelOptionDto {
    id: number;
    value: string;
}
@Injectable({ providedIn: 'root' })
export class WarehouseService {
    // Private
    private _filters: BehaviorSubject<any> = new BehaviorSubject({});
    private _pagination: BehaviorSubject<Pagination> = new BehaviorSubject(null);
    private _product: BehaviorSubject<any | null> = new BehaviorSubject(null);
    private _products: BehaviorSubject<any[] | null> = new BehaviorSubject(null);
    private _warehouse: BehaviorSubject<Warehouse[]> = new BehaviorSubject<Warehouse[]>(null);
    private controller: string = 'WarehouseLocation';
    private withJoin: string = 'countryCurrency';
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _oDataService: oDataService,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for filters
     */
    get filters() {
        return this._filters.value;
    }

    /**
     * Setter for filters
     */
    set filters(values: any) {
        this._filters.next(values);
    }

    /**
     * Getter for Pagination
     */
    get pagination() {
        return this._pagination.value;
    }

    /**
     * Setter for Pagination
     */
    set pagination(values) {
        this._pagination.next(values);
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<Pagination> {
        return this._pagination.asObservable();
    }

    /**
     * Getter for product
     */
    get product$(): Observable<any> {
        return this._product.asObservable();
    }

    /**
     * Getter for products
     */
    get products$(): Observable<any[]> {
        return this._products.asObservable();
    }

    /**
     * Getter for Observable country and currency
     */
    get warehouse$(): Observable<Warehouse[]> {
        return this._warehouse.asObservable();
    }

    /**
     * Getter for country and currency
     */
    get warehouse(): Warehouse[] {
        return this._warehouse.value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set BehaviorSubject Country
     * @param value
     */
    setWarehouse$(value: Warehouse[]) {
        this._warehouse.next(value);
    }

    /**
     * Get products
     * @param reload
     */
    getProductsWithPagination(reload?: boolean): Observable<any> {
        if (reload) {
            this.pagination = {
                length: 0,
                page: 0,
                size: 100,
                sort: '',
                order: 'asc'
            }
            this.filters = {};
        }
        return forkJoin({
            products: this.getProducts(this.filters, this.pagination),
        }).pipe(
            tap((response) => {
                this._pagination.next({ ...this.pagination, length: response.products["@odata.count"] });
                this._products.next(response.products["value"]);
            })
        );
    }

    getCommonData() {
        return this.getProducts({ isActive: true }, null).pipe(
            map((response) => {
                let resp = response["value"];
                resp.sort((a, b) => {
                    return a.locationCode < b.locationCode ? -1 : 1;
                });
                this._warehouse.next(resp);
                return resp;
            })
        );
    }

    /**
     * Get products
     *
     * @param filters
     * @param pagination
     */
    getProducts(filters, pagination) {
        return this._httpClient.get<any[]>(`${environment.oDataUrl}/odata/` + this.controller + this._oDataService.setFilters(filters, pagination));
    }

    /**
     * download products
     * 
     * @param filters 
     */
    downloadProducts(filters): Observable<any[]> {
        return this._httpClient.get<any[]>(`${environment.oDataUrl}/odata/` + this.controller
            + this._oDataService.setFilters(filters, null))
            .pipe(
                map((response) => {
                    return response["value"];
                })
            );
    }

    /**
     * Upload product
     * 
     * @param products
     */
    uploadProduct(products) {
        return this._httpClient.post(`${environment.oDataUrl}/api/` + this.controller, products, {
            reportProgress: true,
            observe: 'events'
        });
    }

    /**
     * Create product
     * 
     * @param products
     */
    createProduct(products) {
        this._httpClient.post(`${environment.oDataUrl}/api/` + this.controller, products).subscribe(() => {
            forkJoin({
                items: this.getProductsWithPagination(),
                commondata: this.getCommonData()
            }).subscribe();
        });
    }

    /**
     * Update product
     *
     * @param product
     */
    updateProduct(id: string, product: any) {
        this._httpClient.put(`${environment.oDataUrl}/api/` + this.controller + `/${id}`, product).subscribe(() => {
            forkJoin({
                items: this.getProductsWithPagination(),
                commondata: this.getCommonData()
            }).subscribe();
        });
    }

    getOrLoadWarehouses(): Observable<Warehouse[]> {
        if (!this._warehouse.value) {
            return this.getCommonData().pipe(
                map(() => this._warehouse.value), // emit the loaded value
                filter(val => !!val)              // ensure not null
            );
        }
        return this._warehouse.asObservable();
    }
    getLevelData(level: number, countryCode: string, l1?: string, l2?: string, l3?: string, l4?: string): Observable<string[]> {
        let params = new HttpParams()
            .set('level', level.toString())
            .set('countryCode', countryCode);

        if (l1) params = params.set('l1', l1);
        if (l2) params = params.set('l2', l2);
        if (l3) params = params.set('l3', l3);
        if (l4) params = params.set('l4', l4);

        return this._httpClient.get<string[]>(`${environment.oDataUrl}/api/${this.controller}/getleveldata`, { params });
    }

    getLevelDataWithId(level: number, countryCode: string, l1?: string, l2?: string, l3?: string, l4?: string): Observable<LevelOptionDto[]> {
        let params = new HttpParams()
            .set('level', level.toString())
            .set('countryCode', countryCode);

        if (l1) params = params.set('l1', l1);
        if (l2) params = params.set('l2', l2);
        if (l3) params = params.set('l3', l3);
        if (l4) params = params.set('l4', l4);

        return this._httpClient.get<LevelOptionDto[]>(`${environment.oDataUrl}/api/${this.controller}/getleveldata-with-id`, { params });
    }

    /**
     * Sends the new level value from the pop-up to the backend
     */
    addLevelOption(payload: any): Observable<any> {
        // payload matches the AddLevelRequest C# class
        return this._httpClient.post(`${environment.oDataUrl}/api/${this.controller}/addnewlevel`, payload);
    }
    getLocationCodesByLevels(countryId?: number, l1?: string, l2?: string, l3?: string, l4?: string,l5?: string,l6?: string): Observable<LocationLookupDto[]> {
        let params = new HttpParams();
        if (countryId) params = params.set('countryId', countryId.toString());
        if (l1) params = params.set('l1', l1);
        if (l2) params = params.set('l2', l2);
        if (l3) params = params.set('l3', l3);
        if (l4) params = params.set('l4', l4);
        if (l5) params = params.set('l5', l5);
        if (l6) params = params.set('l6', l6);

        return this._httpClient.get<LocationLookupDto[]>(
            `${environment.oDataUrl}/api/${this.controller}/getlocationcodesbylevel`,
            { params }
        );
    }
    getCodesMulti(countryIds: number[], l1: string[], l2: string[], l3: string[], l4: string, l5: string, l6: string): Observable<LocationLookupDto[]> {
    let params = new HttpParams();
    
    // Append arrays
    countryIds?.forEach(id => params = params.append('countryIds', id.toString()));
    l1?.forEach(v => params = params.append('l1', v));
    l2?.forEach(v => params = params.append('l2', v));
    l3?.forEach(v => params = params.append('l3', v));
    
    // Append single values
    if (l4) params = params.set('l4', l4);
    if (l5) params = params.set('l5', l5);
    if (l6) params = params.set('l6', l6);
    return this._httpClient.get<LocationLookupDto[]>(`${environment.oDataUrl}/api/${this.controller}/getlocationcodes-multi`, { params });
}

getLevelDataMulti(level: number, countryCode: string, l1: string[], l2: string[], l3: string[], l4?: string, l5?: string): Observable<string[]> {
    let params = new HttpParams()
        .set('level', level.toString())
        .set('countryCode', countryCode);

    l1?.forEach(v => params = params.append('l1', v));
    l2?.forEach(v => params = params.append('l2', v));
    l3?.forEach(v => params = params.append('l3', v));
    if (l4) params = params.set('l4', l4);
    if (l5) params = params.set('l5', l5);

    return this._httpClient.get<string[]>(`${environment.oDataUrl}/api/${this.controller}/getleveldata-multi`, { params });
}
}
